import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Eye, EyeOff, Check } from "lucide-react";
import AuthLayout, { formatApiError } from "@/components/AuthLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      await refresh();
      toast.success("Password reset. You're signed in.");
      navigate("/grove");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const pwOk = password.length >= 8;

  return (
    <AuthLayout
      title="Set a new password."
      subtitle="At least 8 characters. Anything else is up to you — pick something you'll actually remember."
      footer={
        <>
          <Link to="/login" className="text-[#4E5A3D] underline underline-offset-2">Back to sign in</Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#2A2823]/60" htmlFor="reset-password">New password</label>
          <div className="mt-1 relative">
            <input
              id="reset-password"
              data-testid="reset-password-input"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
              placeholder="at least 8 characters"
              autoComplete="new-password"
              className="w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2.5 pr-11 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
              required
            />
            <button
              type="button"
              data-testid="reset-toggle-password"
              onClick={() => setShowPw((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A2823]/50 hover:text-[#2A2823]"
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {password.length > 0 && (
            <div className={`mt-1.5 flex items-center gap-1.5 text-xs ${pwOk ? "text-[#4E5A3D]" : "text-[#2A2823]/60"}`}>
              {pwOk ? <Check className="w-4 h-4" /> : <div className="w-4 h-4" />}
              <span>{pwOk ? "Password looks good." : `${password.length}/8 minimum`}</span>
            </div>
          )}
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#2A2823]/60" htmlFor="reset-confirm">Confirm password</label>
          <input
            id="reset-confirm"
            data-testid="reset-confirm-input"
            type={showPw ? "text" : "password"}
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); if (error) setError(""); }}
            placeholder="type it again"
            autoComplete="new-password"
            className="mt-1 w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
            required
          />
        </div>

        {error && (
          <div
            data-testid="reset-error"
            className="text-sm text-[#8a3a2a] bg-[#8a3a2a]/10 border border-[#8a3a2a]/25 rounded-2xl px-4 py-2.5"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          data-testid="reset-submit"
          disabled={submitting || !password || !confirm}
          className="w-full rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-12 text-base shadow-none disabled:opacity-60"
        >
          {submitting ? "Saving…" : "Set new password"}
        </Button>
      </form>
    </AuthLayout>
  );
}
